export const isUndefined = (o) => {
	return (typeof o === "undefined");
};

export const isNullOrUndefined = (o) => {
	return isUndefined(o) || (o === null);
};

export const isEmptyString = (s) => {
	return isNullOrUndefined(s) || ((`${s}`?.trim()?.length || 0) < 1);
};

export const isEqualText = (a, b) => {
	return ((a || "").trim().toLowerCase()) === ((b || "").trim().toLowerCase());
};

export const isArray = (a) => {
	if (!isNullOrUndefined(a) && (Object.prototype.toString.call(a) === "[object Array]")) {
		return true;
	}
	return false;
};

export const isEmptyArray = (a) => {
	return (isArray(a) === false) || (a.length < 1);
};


/**
 * Validates if the provided email address is in a valid format.
 * @param {string} email - The email address to validate.
 * @returns {boolean} True if the email is valid, false otherwise.
 */
export const isValidEmail = (email) => {
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,10}$/;
	return (emailRegex || "").test(email);
}

/**
 * Returns a promise that resolves after the specified number of milliseconds.
 * If the wait time is less than 1 millisecond, it resolves immediately.
 * @param {number} waitMs - The number of milliseconds to wait before resolving the promise.
 * @returns {Promise<void>} A promise that resolves after the specified wait time.
 */
export const delay = (waitMs) => {
	return new Promise((resolve) => {
		if (waitMs < 1) {
			resolve();
		} else {
			setTimeout(resolve, waitMs);
		}
	});
};

const dig = (obj, targetPropertyName) =>
	targetPropertyName in obj
		? obj[targetPropertyName]
		: Object.values(obj).reduce((acc, val) => {
			if (acc !== undefined) return acc;
			if (typeof val === 'object') return dig(val, targetPropertyName);
		}, undefined);

/**
 * Recursively searches for a property in an object and returns its value.
 * If the property is not found, it returns undefined.
 * @param {Object} obj - The object to search.
 * @param {string} targetPropertyName - The name of the property to find.
 * @returns {*} The value of the property if found, otherwise undefined.
 */
export const digProperty = (obj, targetPropertyName) => {
	if (isNullOrUndefined(obj) || isEmptyString(targetPropertyName)) {
		return undefined;
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => dig(item, targetPropertyName));
	}

	if (typeof obj !== 'object') {
		return undefined;
	}

	return dig(obj, targetPropertyName);
}

/**
 * Safely parses a JSON string and returns the parsed object.
 * If the string is not valid JSON, it returns undefined.
 * @param {string} jsonText - The JSON string to parse.
 * @returns {Object|undefined} The parsed JSON object or undefined if parsing fails.
 */
export const safeParseTextAsJson = (jsonText) => {
	const json = !!((jsonText || "").trim().match(/^{[\s\S]*}$/gi)) ? JSON.parse(jsonText) : undefined;
	return json;
};

/**
 * Safely parses a JSON string and returns the parsed object.
 * If the string is not valid JSON, it returns undefined.
 * @param {string} jsonText - The JSON string to parse.
 * @returns {Object|undefined} The parsed JSON object or undefined if parsing fails.
 */
export const safeGetTextAsJson = (jsonText) => {
	return safeParseTextAsJson(jsonText);
};

export const safeGetResponseBodyAsText = async (response) => {
	if (!response) { return undefined; }
	try {
		const t = await response.text();
		return `${t || ""}`.trim();
	} catch {
		return undefined;
	}
};

export const safeGetResponseBodyAsJson = async (response) => {
	const responseText = await safeGetResponseBodyAsText(response);
	const json = !!((responseText || "").match(/^{[\s\S]*}$/gi)) ? JSON.parse(responseText) : null;
	return json;
};

